import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';

export default function BookingPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { flight, passengers = 1, travelClass = 'economy' } = state || {};

  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [passengerForms, setPassengerForms] = useState(
    Array.from({ length: parseInt(passengers) }, () => ({
      firstName: '', lastName: '', dob: '', passport: '', gender: 'male',
    }))
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!flight) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <p className="text-gray-500 text-lg">No flight selected. Please search for flights first.</p>
        <button onClick={() => navigate('/')} className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg">
          Go Home
        </button>
      </div>
    );
  }

  const updatePassenger = (index, field, value) => {
    const updated = [...passengerForms];
    updated[index] = { ...updated[index], [field]: value };
    setPassengerForms(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post('/api/bookings', {
        flight,
        passengers: passengerForms,
        contactEmail,
        contactPhone,
        travelClass,
        totalAmount: flight.totalPrice,
      });
      navigate('/confirmation', { state: { booking: res.data.booking } });
    } catch (err) {
      setError(err.response?.data?.error || 'Booking failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const classLabel = { economy: 'Economy', business: 'Business', firstClass: 'First Class' }[travelClass];

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <SEO
        title="Complete Your Booking"
        description="Enter your passenger details to complete your flight booking on USAFlightFare. Fast, secure checkout with instant e-ticket confirmation."
        noindex={true}
      />
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Complete Your Booking</h1>

      {/* Flight Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6">
        <h2 className="font-semibold text-blue-700 mb-3">✈️ Flight Summary</h2>
        <div className="flex flex-col md:flex-row justify-between gap-4">
          <div>
            <p className="font-bold text-gray-800 text-lg">
              {flight.originCity} ({flight.origin}) → {flight.destinationCity} ({flight.destination})
            </p>
            <p className="text-gray-600">{flight.airline} • {flight.flightNumber} • {flight.date}</p>
            <p className="text-gray-600">{flight.departureTime} → {flight.arrivalTime} • {flight.duration}</p>
            <p className="text-gray-600">Class: {classLabel} • Baggage: {flight.baggage}</p>
          </div>
          <div className="text-right">
            <p className="text-gray-500 text-sm">{passengers} passenger{parseInt(passengers) > 1 ? 's' : ''}</p>
            <p className="text-2xl font-bold text-blue-600">${flight.totalPrice?.toLocaleString('en-US')}</p>
            <p className="text-gray-400 text-sm">Total Amount</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Passenger Details */}
        {passengerForms.map((pax, i) => (
          <div key={i} className="bg-white rounded-xl shadow p-5 mb-4 border border-gray-100">
            <h3 className="font-semibold text-gray-800 mb-4">Passenger {i + 1}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <input
                  type="text"
                  value={pax.firstName}
                  onChange={e => updatePassenger(i, 'firstName', e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="John"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <input
                  type="text"
                  value={pax.lastName}
                  onChange={e => updatePassenger(i, 'lastName', e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Doe"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender *</label>
                <select
                  value={pax.gender}
                  onChange={e => updatePassenger(i, 'gender', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
                <input
                  type="date"
                  value={pax.dob}
                  onChange={e => updatePassenger(i, 'dob', e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Passport / ID No.</label>
                <input
                  type="text"
                  value={pax.passport}
                  onChange={e => updatePassenger(i, 'passport', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Optional"
                />
              </div>
            </div>
          </div>
        ))}

        {/* Contact Details */}
        <div className="bg-white rounded-xl shadow p-5 mb-6 border border-gray-100">
          <h3 className="font-semibold text-gray-800 mb-4">Contact Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
              <input
                type="email"
                value={contactEmail}
                onChange={e => setContactEmail(e.target.value)}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="your@email.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              <input
                type="tel"
                value={contactPhone}
                onChange={e => setContactPhone(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="+91 9876543210"
              />
            </div>
          </div>
        </div>

        {error && <p className="text-red-500 mb-4">⚠️ {error}</p>}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-bold py-4 rounded-xl text-lg transition-colors shadow-lg"
        >
          {loading ? 'Processing...' : `Confirm Booking • $${flight.totalPrice?.toLocaleString('en-US')}`}
        </button>
      </form>
    </div>
  );
}
